import React, { useEffect, useRef, useState } from 'react';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Loader2, Send, GraduationCap, RotateCw, Trophy } from 'lucide-react';
import api, { apiError } from '../../lib/api';
import { DEMO } from '../../lib/demo';
import AcademyDrills from './AcademyDrills';

// ENTRAÎNEMENT DES CHATTEURS (05/09) — remplace le site externe « Chat Academy » :
// un faux fan joué par l'IA, puis une note sur les mêmes critères que les rapports hebdo.
// Aucune vraie conversation n'est touchée.
export default function Training() {
  const [profils, setProfils] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [profil, setProfil] = useState('nouveau');
  const [account, setAccount] = useState('');
  const [sess, setSess] = useState(null);
  const [draft, setDraft] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [demarrage, setDemarrage] = useState(false);
  const [historique, setHistorique] = useState([]);
  const [onglet, setOnglet] = useState('scenarios');  // scénarios = l'entraînement principal
  const finRef = useRef(null);

  const chargerHistorique = () => api.get('/training/sessions')
    .then(({ data }) => setHistorique(data.sessions || [])).catch(() => {});

  useEffect(() => {
    if (DEMO) return;
    api.get('/training/profils').then(({ data }) => {
      const l = data.profils || [];
      setProfils(l);
      // Les types viennent maintenant des vraies conversations : « nouveau » peut
      // ne pas en faire partie si l'agence n'a pas assez de fans de ce genre.
      setProfil(p => (l.some(x => x.id === p) ? p : (l[0]?.id || p)));
    }).catch(() => {});
    api.get('/accounts').then(({ data }) => {
      const prets = (data || []).filter(a => a.status === 'ready');
      setAccounts(prets);
      setAccount(prets[0]?.id || '');
    }).catch(() => {});
    chargerHistorique();
  }, []);

  useEffect(() => { finRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [sess?.messages?.length]);

  const demarrer = async () => {
    setDemarrage(true);
    try {
      const { data } = await api.post('/training/start', { profil, account_id: account });
      setSess(data);
      setDraft('');
    } catch (e) { toast.error(apiError(e, 'Impossible de démarrer')); }
    finally { setDemarrage(false); }
  };

  const envoyer = async () => {
    const t = draft.trim();
    if (!t || !sess) return;
    setDraft('');
    // Affichage immédiat de son message pendant que le fan « réfléchit ».
    setSess(s => ({ ...s, messages: [...(s.messages || []), { role: 'chatteuse', texte: t }] }));
    setEnvoi(true);
    try {
      const { data } = await api.post(`/training/${sess.id}/message`, { texte: t });
      setSess(s => ({ ...s, messages: data.messages }));
    } catch (e) { toast.error(apiError(e, 'Échec de l\'envoi')); }
    finally { setEnvoi(false); }
  };

  const terminer = async () => {
    if (!sess) return;
    setEnvoi(true);
    try {
      const { data } = await api.post(`/training/${sess.id}/finish`);
      setSess(data);
      chargerHistorique();
    } catch (e) { toast.error(apiError(e, 'Échec de la notation')); }
    finally { setEnvoi(false); }
  };

  const ev = sess?.evaluation;
  const noteCouleur = (n) => (n >= 15 ? 'text-emerald-300' : n >= 10 ? 'text-amber-300' : 'text-rose-300');

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto fade-in" data-testid="training-page">
      <PageHeader title="Entraînement"
        subtitle="Entraîne-toi sans toucher un seul vrai client" />

      <div className="inline-flex rounded-xl border border-white/10 bg-white/[0.03] p-1 mb-4">
        <button onClick={() => setOnglet('scenarios')} data-testid="tab-scenarios"
          className={`px-4 py-1.5 text-sm rounded-lg transition-all ${onglet === 'scenarios' ? 'bg-indigo-500 text-white' : 'text-slate-400 hover:text-white'}`}>
          Scénarios notés
        </button>
        <button onClick={() => setOnglet('conversation')} data-testid="tab-conversation"
          className={`px-4 py-1.5 text-sm rounded-lg transition-all ${onglet === 'conversation' ? 'bg-indigo-500 text-white' : 'text-slate-400 hover:text-white'}`}>
          Conversation complète
        </button>
      </div>

      {onglet === 'scenarios' ? <AcademyDrills /> : (<>

      {!sess && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
          <div className="text-sm text-slate-300">Choisis le type de fan à affronter :</div>
          {profils.some(p => p.reel) && (
            <div className="text-[11px] text-slate-400 -mt-2">
              Ces types sont construits sur les <b className="text-slate-300">vraies conversations</b> de l'agence
              (120 derniers jours) : le faux fan reprend leur façon d'écrire, leurs objections, et ouvre
              avec un vrai premier message.
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {profils.map(p => (
              <button key={p.id} onClick={() => setProfil(p.id)} data-testid={`profil-${p.id}`}
                className={`rounded-xl border p-3 text-left text-[13px] transition-colors ${profil === p.id
                  ? 'border-indigo-400/60 bg-indigo-500/[0.12] text-white'
                  : 'border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/25'}`}>
                <div>{p.nom}</div>
                {p.reel && (
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {p.nb_fans} fan{p.nb_fans > 1 ? 's' : ''} réel{p.nb_fans > 1 ? 's' : ''}
                    {p.depense_mediane ? ` · ${p.depense_mediane} € médian` : ''}
                  </div>
                )}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[12px] text-slate-400">Tu joues :</span>
            <select value={account} onChange={e => setAccount(e.target.value)}
              className="bg-white/5 border border-white/10 rounded-lg px-3 h-9 text-xs text-white outline-none">
              {accounts.map(a => <option key={a.id} value={a.id} className="bg-[#0B0B0E]">{a.name}</option>)}
            </select>
            <Button onClick={demarrer} disabled={demarrage} className="bg-indigo-500 hover:bg-indigo-600 ml-auto">
              {demarrage ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <GraduationCap size={14} className="mr-1.5" />}
              Commencer
            </Button>
          </div>
        </div>
      )}

      {sess && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <div className="px-4 py-2.5 border-b border-white/5 flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-white">{sess.profil_nom}</span>
            <span className="text-[11px] text-slate-500">· tu joues {sess.modele}</span>
            {sess.statut === 'en_cours' ? (
              <Button size="sm" variant="outline" onClick={terminer} disabled={envoi}
                className="ml-auto h-7 border-white/15 bg-white/5 text-slate-200 text-[12px]">
                Terminer et me noter
              </Button>
            ) : (
              <Button size="sm" onClick={() => setSess(null)} className="ml-auto h-7 bg-indigo-500 hover:bg-indigo-600 text-[12px]">
                <RotateCw size={12} className="mr-1.5" /> Nouvelle session
              </Button>
            )}
          </div>

          <div className="p-4 space-y-2.5 max-h-[52vh] overflow-y-auto scrollbar-thin">
            {(sess.messages || []).map((m, i) => (
              <div key={i} className={`flex ${m.role === 'chatteuse' ? 'justify-end' : ''}`}>
                <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed ${m.role === 'chatteuse'
                  ? 'bg-indigo-600/90 text-indigo-50 rounded-br-md'
                  : 'bg-white/[0.06] text-slate-200 rounded-bl-md'}`}>
                  {m.texte}
                </div>
              </div>
            ))}
            {envoi && sess.statut === 'en_cours' && (
              <div className="text-[11px] text-slate-500 flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> il écrit…</div>
            )}
            <div ref={finRef} />
          </div>

          {sess.statut === 'en_cours' ? (
            <div className="p-3 border-t border-white/5 flex gap-2">
              <Input data-testid="training-input" value={draft} onChange={e => setDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); envoyer(); } }}
                placeholder="Écris comme si c'était un vrai fan…"
                className="bg-[#18171C] border-white/10 text-white h-10 text-sm rounded-xl" />
              <Button onClick={envoyer} disabled={envoi || !draft.trim()} className="bg-indigo-500 hover:bg-indigo-600 h-10">
                <Send size={15} />
              </Button>
            </div>
          ) : ev && (
            <div className="p-4 border-t border-white/5 space-y-3">
              <div className="flex items-center gap-3">
                <Trophy size={18} className="text-amber-300" />
                <span className={`text-2xl font-bold ${noteCouleur(ev.note)}`}>{ev.note}/20</span>
                <span className="text-[13px] text-slate-300">{ev.resume}</span>
              </div>
              {(ev.points_forts || []).length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold text-emerald-300 mb-1">Ce qui a marché</div>
                  <ul className="space-y-1">{ev.points_forts.map((x, i) => (
                    <li key={i} className="text-[12px] text-slate-300 flex gap-2"><span className="text-emerald-400">•</span><span>{x}</span></li>))}
                  </ul>
                </div>
              )}
              {(ev.a_corriger || []).length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold text-rose-300 mb-1">À corriger</div>
                  <ul className="space-y-1">{ev.a_corriger.map((x, i) => (
                    <li key={i} className="text-[12px] text-slate-300 flex gap-2"><span className="text-rose-400">•</span><span>{x}</span></li>))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {historique.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 mt-5">
          <div className="text-sm font-semibold text-white mb-2">Sessions passées</div>
          <div className="space-y-1.5">
            {historique.slice(0, 12).map(h => (
              <div key={h.id} className="flex items-center gap-2 flex-wrap text-[12px] text-slate-400 border-b border-white/5 pb-1.5 last:border-0">
                <span className="text-slate-200">{h.chatter_name || '—'}</span>
                <span>· {h.profil_nom}</span>
                <span>· {h.started_at ? new Date(h.started_at + 'Z').toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : ''}</span>
                {h.evaluation?.note != null
                  ? <span className={`ml-auto font-semibold ${noteCouleur(h.evaluation.note)}`}>{h.evaluation.note}/20</span>
                  : <span className="ml-auto text-slate-600">non terminée</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
